import { toast } from "sonner";
import type { QueryClient } from "@tanstack/react-query";
import {
  getEmailLog,
  getGetEmailLogQueryKey,
  getListEmailLogsQueryKey,
} from "@workspace/api-client-react";
import { navigate } from "wouter/use-browser-location";

// `POST /api/send` returns 202 with the row in `status: "queued"`; the
// QStash worker flips it to `sent` (with `providerMessageId`) or
// `failed` (with `errorMessage`) shortly after. Without polling, a
// failure on the worker side is silent — the user only sees the
// upbeat "queued" toast and would have to navigate to the Logs page
// to notice the red Failed badge. This helper polls the just-queued
// log row in the background and surfaces an error toast (with the
// stored `errorMessage` and a "View" action linking to the log
// detail) whenever the worker reports `failed`. The happy path
// (`sent`) emits no extra toast — the existing "queued" toast plus
// the eventual Sent badge in /app/logs already cover it.

const POLL_INTERVAL_MS = 2_500;
// ~60s of polling: long enough to catch the typical worker latency
// (sub-second to a few seconds) and most provider stalls, while
// bounded so a stuck queued row doesn't keep a timer alive forever.
const MAX_ATTEMPTS = 24;

// Dedup tracker so two rapid calls for the same logId (e.g. a
// re-mount) don't run two concurrent polling loops.
const inFlight = new Set<number>();

export interface WatchPendingSendOpts {
  logId: number;
  to: string;
  queryClient: QueryClient;
}

export function watchPendingSend(opts: WatchPendingSendOpts): void {
  const { logId, to, queryClient } = opts;
  if (!Number.isFinite(logId) || logId <= 0) return;
  if (inFlight.has(logId)) return;
  inFlight.add(logId);

  let attempts = 0;
  const tick = async () => {
    attempts += 1;
    try {
      const log = await getEmailLog(logId);
      if (log.status === "queued") {
        if (attempts >= MAX_ATTEMPTS) {
          inFlight.delete(logId);
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }

      // Final state — refresh cached views so the Logs list and any
      // open log detail page reflect the new status without a manual
      // refetch.
      queryClient.invalidateQueries({ queryKey: getListEmailLogsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetEmailLogQueryKey(logId) });
      inFlight.delete(logId);

      if (log.status === "failed") {
        notifyFailure({
          logId,
          to,
          errorMessage: log.errorMessage ?? null,
          providerErrorMessage: log.providerErrorMessage ?? null,
        });
      }
      // status === "sent" → no extra toast; the prior "queued"
      // toast and the Sent badge in /app/logs cover the happy path.
    } catch {
      // Network blip / 401 between polls — back off and try again,
      // but stop after the same overall attempts ceiling so a broken
      // session doesn't poll forever.
      if (attempts >= MAX_ATTEMPTS) {
        inFlight.delete(logId);
        return;
      }
      setTimeout(tick, POLL_INTERVAL_MS);
    }
  };

  setTimeout(tick, POLL_INTERVAL_MS);
}

export interface NotifyFailureOpts {
  logId: number;
  to: string;
  errorMessage: string | null;
  providerErrorMessage: string | null;
}

// Shared toast renderer so the immediate-failure path in Compose
// (response came back with `status: "failed"` already) and the
// polled-failure path here render an identical notification.
export function notifyFailure(opts: NotifyFailureOpts): void {
  const { logId, to, errorMessage, providerErrorMessage } = opts;
  const reason =
    errorMessage?.trim() || "The email provider rejected the send.";
  const description = providerErrorMessage?.trim()
    ? providerErrorMessage.trim()
    : undefined;
  toast.error(`Send to ${to} failed: ${reason}`, {
    duration: 15000,
    description,
    action: {
      label: "View log",
      onClick: () => {
        navigate(`/app/logs/${logId}`);
      },
    },
  });
}
